<template>
    <div>
        <FullscreenLoading v-if="isLoading" />

        <ValidationObserver v-slot="{handleSubmit}" ref="formRef" v-bind="$attrs">
            <form @submit.prevent="handleSubmit(onSubmit)" :id="id">
                <ValidationProvider vid="text" :name="$t('welcome_sign_overrides.text')" rules="required" v-slot="{ errors }">
                    <div class="form-group row">
                        <label class="form-control-label col-4">{{ $t('welcome_sign_overrides.text') }}*</label>
                        <div class="col-8">
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                v-model="formValues.text"
                                :placeholder="$t('welcome_sign_overrides.text')"
                                :class="{ 'is-invalid': errors.length }"
                                :disabled="disabled"
                                required />
                            <div class="invalid-feedback d-block" v-if="errors.length">
                                {{ errors[0] }}
                            </div>
                        </div>
                    </div>
                </ValidationProvider>

                <ValidationProvider vid="start" :name="$t('welcome_sign_overrides.from')" rules="required" v-slot="{ errors }">
                    <div class="form-group row">
                        <label class="form-control-label col-4">{{ $t('welcome_sign_overrides.from') }}*</label>
                        <div class="col-8">
                            <datetime-picker
                                v-model="formValues.start"
                                id="start"
                                type="datetime"
                                :disabled="disabled"
                                inputClass="form-control form-control-sm"
                                class="theme-company"
                                :format="'dd-MM-yyyy HH:mm'"
                            />

                            <div class="invalid-feedback d-block" v-if="errors.length">
                                {{ errors[0] }}
                            </div>
                        </div>
                    </div>
                </ValidationProvider>

                <ValidationProvider vid="end" :name="$t('welcome_sign_overrides.until')" rules="required" v-slot="{ errors }">
                    <div class="form-group row">
                        <label class="form-control-label col-4">{{ $t('welcome_sign_overrides.until') }}*</label>
                        <div class="col-8">
                            <datetime-picker
                                v-model="formValues.end"
                                id="end"
                                type="datetime"
                                :disabled="disabled"
                                inputClass="form-control form-control-sm"
                                class="theme-company"
                                :format="'dd-MM-yyyy HH:mm'"
                            />

                            <div class="invalid-feedback d-block" v-if="errors.length">
                                {{ errors[0] }}
                            </div>
                        </div>
                    </div>
                </ValidationProvider>

                <ValidationProvider vid="welcome_sign_id" :name="$t('welcome_sign_overrides.welcome_sign')" rules="required" v-slot="{ errors }">
                    <div class="form-group row">
                        <label class="form-control-label col-4">{{ $t('welcome_sign_overrides.welcome_sign') }}*</label>
                        <div class="col-8">
                            <WelcomeSignSelect
                                v-model="formValues.welcome_sign_id"
                                id="welcome_sign_id"
                                url="/api/welcome-sign-overrides/welcome-signs/for-select"
                            />

                            <div class="invalid-feedback d-block" v-if="errors.length">
                                {{ errors[0] }}
                            </div>
                        </div>
                    </div>
                </ValidationProvider>

                <div class="form-group row">
                    <label class="form-control-label col-6">
                        <h5>{{ $t('welcome_sign_overrides.background_image') }}</h5>

                        <div class="background-file-upload">
                            <!-- todo: when vue3 is implemented, replace below with the upload component in vue3-->
                            <label v-if="canShowBackgroundImageUpload" class="upload-area">
                                {{ $t('welcome_sign_overrides.select_a_background_image') }}
                                <input
                                    type="file"
                                    class="d-none"
                                    accept="image/*"
                                    @change="selectBackgroundImage($event)" />
                            </label>

                            <div v-else class="background-file-preview">
                                <span class="remove-background-file" type="button" @click.prevent="formValues.background_image = null; canShowBackgroundImageUpload = true">
                                    &times;
                                </span>

                                <img v-if="formValues.background_image" :src="formValues.background_image" class="img-fluid">
                                <img v-else :src="welcomeSignOverride.image_path" class="img-fluid">
                            </div>
                        </div>
                    </label>
                    <div class="col-6" v-if="store.state.companySettings.welcome_sign_music_enabled">
                        <h5>{{ $t('welcome_sign_overrides.songs') }}</h5>

                        <div v-for="(formSong, index) of formSongs" :key="index" class="mb-4">
                            <div class="row align-items-center mb-3 shadow-none file-block-inner">
                                <div class="col-auto song-block-inner">
                                    <div
                                        class="icon icon-shape bg-soft-warning text-warning mr-2"
                                        style="background-size: cover"
                                        :style="{ backgroundImage: formSong.song.cover ? 'url(\'' + formSong.song.cover_public_path + '\')' : null}">
                                        <icon v-if="!formSong.song.cover" name="music" style="fill: #FFBE3D;"></icon>
                                    </div>
                                </div>
                                <div class="col pl-0">
                                    <div style="line-height: 14px;">
                                        <span class="h6 text-sm mb-0 song-block-inner">{{ formSong.song.display_name || formSong.song.name }}</span>
                                        <br />
                                        <p class="d-inline-block mb-0 text-xs song-block-inner">{{ formSong.song.author }}</p>
                                    </div>
                                </div>

                                <div class="col-auto actions">
                                    <div class="dropdown" data-toggle="dropdown">
                                        <a href="#" class="action-item" role="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                            <icon name="times" @click="removeSong(formSong)"></icon>
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <SongPlayer :preload="'none'" :src="formSong.song.public_path" :key="index" />
                        </div>

                        <div class="text-right mt-4">
                            <button type="button" class="btn btn-xs btn-primary" @click.prevent="showSongModal = true">{{ $t('welcome_sign_overrides.open_song_library') }}</button>
                        </div>
                    </div>
                </div>
            </form>
        </ValidationObserver>

        <SongModal
            v-if="showSongModal"
            song-url="/api/welcome-sign-overrides/songs"
            @close="showSongModal = false"
            @song-added="addSong"
        />
    </div>
</template>

<script setup>

    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import { computed, inject, onMounted, ref, watch } from 'vue';
    import DatetimePicker from '../../components/DatetimePicker.vue';
    import SongModal from '../../components/SongModal.vue';
    import SongPlayer from '../../components/SongPlayer.vue';
    import { notifyError } from '../../utils/notify.js';
    import Axios from 'axios';
    import FullscreenLoading from '../../components/FullscreenLoading.vue';
    import WelcomeSignSelect from '../../components/selects/WelcomeSignSelect.vue';

    const store = inject('store');
    const emit = defineEmits(['submit']);
    const props = defineProps({
        id: {
            type: String,
            default: null,
        },

        welcomeSignOverride: {
            type: Object,
            default: null,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        formErrors: {
            type: Object,
            default: null,
        },
    });

    const formRef = ref(null);
    const formValues = ref({
        welcome_sign_id: props.welcomeSignOverride?.welcome_sign?.id,

        text: props.welcomeSignOverride?.text,

        start: props.welcomeSignOverride?.start,
        end: props.welcomeSignOverride?.end,

        background_image: null,
        songs: (props.welcomeSignOverride?.songs || []).map(song => song.song.id),
    });

    const isLoading = ref(false);
    const canShowBackgroundImageUpload = ref(!props.welcomeSignOverride?.image_path?.length);
    const showSongModal = ref(false);
    const selectedSongs = ref([]);

    const formSongs = computed(() => {
        return formValues.value.songs.map((songId, index) => {
            return {
                order: index,
                song: selectedSongs.value.find(song => song.id === songId),
            };
        });
    });

    watch(() => props.formErrors, () => {
        formRef.value.setErrors(props.formErrors);
    });

    onMounted(() => {
        loadSelectedSongs();
    });

    function selectBackgroundImage(event) {
        const file = event.target.files[0];

        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = readerEvent => {
            formValues.value.background_image = readerEvent.target.result;
        };
        reader.readAsDataURL(file);

        canShowBackgroundImageUpload.value = false;
    }

    async function addSong(song) {
        formValues.value.songs.push(song.id);
        showSongModal.value = false;

        isLoading.value = true;
        await loadSelectedSongs();
        isLoading.value = false;
    }

    function removeSong({ order, song }) {
        formValues.value.songs.splice(formValues.value.songs.findIndex((songId, index) => {
            return songId === song.id && index === order;
        }), 1);
    }

    async function loadSelectedSongs() {
        if (!formValues.value.songs.length) {
            selectedSongs.value = [];

            return;
        }

        try {
            const response = await Axios.get('/api/welcome-sign-overrides/selected-songs', {
                params: {
                    ids: formValues.value.songs.join(','),
                },
            });

            selectedSongs.value = response.data.data;
        } catch (exception) {
            notifyError(exception);
        }
    }

    function onSubmit() {
        const data = {
            ...formValues.value,

            // Background image:
            //   null = remove
            //   value = set
            //   key not set = do nothing
            background_image: formValues.value.background_image ? formValues.value.background_image : null,
        };

        if (!formValues.value.background_image?.length && !canShowBackgroundImageUpload.value) {
            delete data.background_image;
        }

        emit('submit', data);
    }
</script>

<style lang="scss" scoped>

    .upload-area {
        border: 2px solid #e5e5e5;
        background-color: #ffffff;
        transition: background-color .2s linear;
        width: 100%;
        padding: 20px;
        text-align: center;
        cursor: pointer;
        color: #777;

        &:hover {
            background-color: #f6f6f6;
        }

    }

    .background-file-preview {
        position: relative;

        img {
            width: 100%;
        }

        .remove-background-file {
            position: absolute;
            top: -11px;
            right: -11px;
            width: 20px;
            height: 20px;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 10px;
            color: #718096;
            fill: #718096;
            box-shadow: 2px 3px 5px rgba(31, 45, 61, 0.2);
            z-index: 1;
        }
    }

</style>
